import { createLogger } from '@/lib/logger'
import DOMPurify from 'dompurify'

const logger = createLogger('lib/utils/rich-text-utils')

/**
 * Sanitize HTML content to prevent XSS attacks while preserving image tags
 *
 * @param html The HTML content to sanitize
 * @returns Sanitized HTML
 */
export function sanitizeHtml(html: string | null): string {
  if (!html) return ''
  // Configure DOMPurify to allow rich-text tags and specific attributes
  const purifyConfig = {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'p',
      'a',
      'ul',
      'ol',
      'nl',
      'li',
      'b',
      'i',
      'strong',
      'em',
      'strike',
      's',
      'del',
      'u',
      'code',
      'hr',
      'br',
      'div',
      'table',
      'thead',
      'caption',
      'tbody',
      'tr',
      'th',
      'td',
      'pre',
      'img',
      'span',
      'input',
    ],
    ALLOWED_ATTR: [
      'href',
      'name',
      'target',
      'src',
      'alt',
      'width',
      'height',
      'style',
      'class',
      'type',
      'checked',
      'title',
      'rel',
      'data-s3-key',
      'data-filename',
      'data-user-id',
      'data-user-name',
      'data-attachment',
    ],
    FORBID_TAGS: ['script', 'style', 'iframe', 'frame'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
    ALLOW_DATA_ATTR: true, // Allow data attributes for S3 keys
  }

  return DOMPurify.sanitize(html, purifyConfig)
}

/**
 * Extract S3 keys from rich text HTML content
 *
 * @param html The HTML content to extract keys from
 * @returns Array of S3 keys found in the content
 */
export function extractS3KeysFromHtml(html: string | null): string[] {
  if (!html) return []

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  const images = tempDiv.querySelectorAll('img[data-s3-key]')
  const keys: string[] = []

  images.forEach(img => {
    const key = img.getAttribute('data-s3-key')
    if (key && !keys.includes(key)) {
      keys.push(key)
    }
  })

  return keys
}

/**
 * Replace S3 keys in HTML content with signed URLs
 *
 * @param html The HTML content containing S3 keys
 * @param signedUrls Map of S3 keys to signed URLs
 * @returns HTML with signed URLs replacing S3 keys
 */
export function replaceS3KeysWithUrls(
  html: string | null,
  signedUrls: Record<string, string>,
): string {
  if (!html) return ''

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  const images = tempDiv.querySelectorAll('img[data-s3-key]')

  images.forEach(img => {
    const key = img.getAttribute('data-s3-key')
    if (key && signedUrls[key]) {
      img.setAttribute('src', signedUrls[key])
      // Keep the data-s3-key for future reference
    }
  })

  return tempDiv.innerHTML
}

/**
 * Process HTML content to handle S3 images by fetching signed URLs
 *
 * @param html The HTML content to process
 * @param getSignedUrls Function to fetch signed URLs for keys
 * @returns Promise resolving to HTML with signed URLs
 */
export async function processRichTextImages(
  html: string | null,
  getSignedUrls: (keys: string[]) => Promise<Record<string, string>>,
): Promise<string> {
  if (!html) return ''

  // Extract S3 keys from the content
  const s3Keys = extractS3KeysFromHtml(html)

  if (s3Keys.length === 0) {
    return html // No images to process
  }

  try {
    // Fetch signed URLs for all keys
    const signedUrls = await getSignedUrls(s3Keys)

    // Replace keys with URLs
    return replaceS3KeysWithUrls(html, signedUrls)
  } catch (error) {
    logger.error('Error processing rich text images:', error)
    return html // Return original HTML if processing fails
  }
}

/**
 * Prepare HTML content for rendering by sanitizing it and processing S3 images
 *
 * @param html The HTML content to prepare
 * @param getSignedUrls Function to fetch signed URLs for keys
 * @returns Promise resolving to sanitized HTML ready for rendering
 */
export async function prepareRichTextContent(
  html: string | null,
  getSignedUrls?: (keys: string[]) => Promise<Record<string, string>>,
): Promise<string> {
  if (!html) return ''

  // First sanitize the HTML
  const sanitized = sanitizeHtml(html)

  // If no URL fetcher provided, return sanitized content as-is
  if (!getSignedUrls) {
    return sanitized
  }

  // Process S3 images
  return processRichTextImages(sanitized, getSignedUrls)
}

/**
 * Extract plain text from HTML content
 *
 * @param html The HTML content to extract text from
 * @returns Plain text content
 */
export function extractTextFromHtml(html: string | null): string {
  if (!html) return ''

  // Create a temporary DOM element
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Get the text content
  return tempDiv.textContent || tempDiv.innerText || ''
}

/**
 * Check if HTML content contains any non-empty text
 *
 * @param html The HTML content to check
 * @returns True if the content has non-empty text
 */
export function hasTextContent(html: string | null): boolean {
  if (!html) return false

  // Get plain text
  const text = extractTextFromHtml(html)

  // Check if it contains non-whitespace characters
  return text.trim().length > 0
}

/**
 * Check if HTML content contains any images
 *
 * @param html The HTML content to check
 * @returns True if the content has any images
 */
export function hasImages(html: string | null): boolean {
  if (!html) return false

  // Simple check for img tags
  return html.includes('<img')
}

/**
 * Create a React-friendly safe HTML object for dangerouslySetInnerHTML
 *
 * @param html The HTML content to prepare
 * @returns An object with __html property containing sanitized HTML
 */
export function createSafeHtml(html: string | null) {
  return { __html: sanitizeHtml(html) }
}
